import axios from 'axios';

// Use environment variable to determine the base URL
const baseURL = process.env.REACT_APP_API_URL;

if (!baseURL) {
  console.error('REACT_APP_API_URL is not defined in environment variables');
}

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://172.16.211.132:5000',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const clearSessionAndMaybeRedirect = () => {
  const hadToken = !!localStorage.getItem('token');
  localStorage.removeItem('token');

  if (typeof window === 'undefined') return;

  const currentPath = window.location?.pathname || '/';
  const isOnAuthPage =
    currentPath.startsWith('/login') ||
    currentPath.startsWith('/register') ||
    currentPath.startsWith('/forgot-password') ||
    currentPath.startsWith('/reset-password');

  const isProtectedPath =
    currentPath.startsWith('/dashboard') ||
    currentPath.startsWith('/profile') ||
    currentPath.startsWith('/admin') ||
    currentPath.startsWith('/my-activities');

  // Only hard-redirect when the user had a session and is on a protected page.
  if (hadToken && isProtectedPath && !isOnAuthPage) {
    window.location.href = '/login';
  }
};

// Add request interceptor for authentication
instance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor: try refresh cookie before dropping the session
instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const status = error.response?.status;
    const requestUrl = originalRequest.url || '';

    const isAuthBootstrap = requestUrl.includes('/api/auth/me');
    const isRefreshCall =
      requestUrl.includes('/api/auth/refresh-token') || originalRequest._skipAuthRefresh;
    const isLoginOrLogout =
      requestUrl.includes('/api/auth/login') ||
      requestUrl.includes('/api/auth/logout') ||
      requestUrl.includes('/api/auth/register');

    if (status !== 401 || originalRequest._retry || isRefreshCall || isLoginOrLogout) {
      if (status === 401 && isRefreshCall) {
        clearSessionAndMaybeRedirect();
      } else if (status === 401 && !isAuthBootstrap && !isLoginOrLogout) {
        // Non-refreshable 401 (already retried or no refresh path)
        if (originalRequest._retry) {
          clearSessionAndMaybeRedirect();
        }
      }
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return instance(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const { data } = await instance.post(
        '/api/auth/refresh-token',
        {},
        { _skipAuthRefresh: true }
      );
      const newToken = data?.token;
      if (!newToken) {
        throw new Error('No token returned from refresh');
      }
      localStorage.setItem('token', newToken);
      processQueue(null, newToken);
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return instance(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      if (!isAuthBootstrap) {
        clearSessionAndMaybeRedirect();
      } else {
        localStorage.removeItem('token');
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default instance;
